import React, { useEffect, useMemo, useState } from 'react';
import VendorSideMenuBar from '../VendorLayouts/VendorSideMenuBar';
import VendorTopMenuBar from '../VendorLayouts/VendorTopMenuBar';
import '../../../VendorStyles/VendorAnnouncements.css';
import { useNavigate, useParams } from 'react-router-dom';
import axiosVendorInstance from '../../../axiosVendorInstance';
import axios from 'axios';
import { toast } from 'react-toastify';

const generateId = () => {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch (e) { /* ignore */ }
    return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const defaultForm = {
    title: '',
    message: 'Hello Everyone!\nTaking order for tomorrow.\nPlease place your orders by tonight.',
    items: [],
    orderDeadline: '',
    deliveryStart: '',
    deliveryEnd: '',
    channels: {
        push: true,
        whatsapp: true,
        sms: false,
        feed: true
    },
    status: 'draft'
};

function AnnouncementForm() {
    const navigate = useNavigate();
    const params = useParams();
    const isEdit = !!params.id;
    const [form, setForm] = useState(defaultForm);
    // Manual editor removed; items can be added only from Menu
    const [vendorID, setVendorID] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [cloverMap, setCloverMap] = useState({}); // external_item_id -> { available, qty? }
    const [menuSearch, setMenuSearch] = useState('');

    const toApiDate = (v) => {
        if (!v) return '';
        if (v.includes('T')) {
            const [d, t] = v.split('T');
            const time = t.length === 5 ? `${t}:00` : t;
            return `${d} ${time}`;
        }
        return v;
    };

    const toLocalInput = (v) => {
        if (!v) return '';
        // Expecting 'YYYY-MM-DD HH:mm:ss' => 'YYYY-MM-DDTHH:mm'
        return v.replace(' ', 'T').slice(0, 16);
    };

    useEffect(() => {
        const loadAnnouncementForEdit = async () => {
            if (!isEdit || !vendorID) return;
            try {
                const url = `https://pro-customers-api.omyfood.io/api/announcements/new?vendor_id=${encodeURIComponent(vendorID)}`;
                const res = await axios.get(url);
                const listRaw = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.announcements || []);
                const a = (listRaw || []).find((x) => {
                    const rid = String(params.id);
                    return (
                        String(x?.id) === rid ||
                        String(x?.announcement_id) === rid ||
                        String(x?.notification_id) === rid ||
                        String(x?.announcementId) === rid ||
                        String(x?.notificationId) === rid
                    );
                });
                if (!a) return;
                let items = a.items_json || a.items;
                if (typeof items === 'string') {
                    try { items = JSON.parse(items); } catch (_) { items = []; }
                }
                const mappedItems = Array.isArray(items) ? items.map((it) => ({
                    id: generateId(),
                    name: it.item || it.name || 'Item',
                    price: String(it.price ?? ''),
                    size: '',
                    qtyLeft: typeof it.qty === 'number' ? it.qty : ''
                })) : [];
                const channels = a.channels_json || a.channels;
                setForm({
                    title: a.title || a.message_title || '',
                    message: a.message_text || a.message || '',
                    items: mappedItems,
                    orderDeadline: toLocalInput(a.order_deadline || a.order_deadline_at || ''),
                    deliveryStart: toLocalInput(a.delivery_start || a.delivery_start_at || ''),
                    deliveryEnd: toLocalInput(a.delivery_end || a.delivery_end_at || ''),
                    channels: {
                        push: !!(channels?.mobile_app ?? channels?.push),
                        whatsapp: !!channels?.whatsapp,
                        sms: !!channels?.sms,
                        feed: false
                    },
                    status: a.status || 'draft'
                });
            } catch (e) {
                toast.error('Failed to load announcement');
            }
        };
        loadAnnouncementForEdit();
    }, [isEdit, params.id, vendorID]);

    useEffect(() => {
        const fetchVendor = async () => {
            try {
                const res = await axiosVendorInstance.get('/vendors/account');
                const info = res?.data?.getVendorInfo;
                const id = info && info[0]?.ID;
                if (id) setVendorID(id);
            } catch (e) { /* ignore */ }
        };
        fetchVendor();
    }, []);

    useEffect(() => {
        const fetchMenuAndClover = async () => {
            if (!vendorID) return;
            try {
                const menuRes = await axiosVendorInstance.get(`/vendors/menuitems/vendorpage/${vendorID}`, { skipAuth: true });
                const items = menuRes?.data?.menuItems || [];
                setMenuItems(items);
            } catch (e) { /* ignore */ }

            try {
                const cloverRes = await axios.get(`https://pro-customers-api.omyfood.io/api/cartdetails/clover/itemscategories/${vendorID}`);
                const synced = cloverRes?.data?.syncedItems || [];
                const map = {};
                synced.forEach((si) => {
                    const qtyCandidate = [
                        si.stock, si.stockCount, si.quantity, si.qty, si.availableQty, si.inventory
                    ].find((v) => typeof v === 'number');
                    map[String(si.id)] = {
                        available: !!si.available,
                        qty: typeof qtyCandidate === 'number' ? qtyCandidate : undefined
                    };
                });
                setCloverMap(map);
            } catch (e) { /* ignore */ }
        };
        fetchMenuAndClover();
    }, [vendorID]);

    const updateField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const addFromMenu = (mi) => {
        const extId = String(mi.external_item_id || '');
        const cm = cloverMap[extId] || {};
        const newItem = {
            id: generateId(),
            name: mi.Item_name || mi.Name || 'Unnamed Item',
            price: String(mi.Price ?? ''),
            size: '',
            sourceMenuId: mi.ID,
            externalItemId: extId || undefined,
            available: (mi.Status === 'active') && (cm.available !== false),
            qtyLeft: typeof cm.qty === 'number' ? cm.qty : ''
        };
        setForm((f) => ({ ...f, items: [...(f.items || []), newItem] }));
        toast.success('Item added');
    };

    // Editing is inline on the selected item row

    const handleDeleteItem = (id) => setForm((f) => ({ ...f, items: f.items.filter((i) => i.id !== id) }));

    const buildPayload = (statusOverride) => {
        const items_json = (form.items || []).map((it) => {
            const obj = { item: it.name || 'Item' };
            const p = parseFloat(it.price);
            if (!Number.isNaN(p)) obj.price = p;
            const q = it.qtyLeft === '' ? undefined : Number(it.qtyLeft);
            if (!Number.isNaN(q)) obj.qty = q;
            if (it.sourceMenuId) {
                const mid = Number(it.sourceMenuId);
                obj.menu_item_id = Number.isNaN(mid) ? String(it.sourceMenuId) : mid;
            }
            return obj;
        });
        return {
            title: form.title,
            message_text: form.message,
            order_deadline: toApiDate(form.orderDeadline),
            delivery_start: toApiDate(form.deliveryStart),
            delivery_end: toApiDate(form.deliveryEnd),
            items_json,
            status: statusOverride ?? (form.status || 'draft')
        };
    };

    // const handleSave = async () => {
    //     try {
    //         if (isEdit) {
    //             const payload = buildPayload('draft');
    //             await axios.put(`https://pro-customers-api.omyfood.io/api/announcements/${params.id}`, payload);
    //         } else {
    //             if (!vendorID) {
    //                 toast.error('Missing vendor id');
    //                 return;
    //             }
    //             const payload = { vendor_id: vendorID, ...buildPayload(form.status || 'draft') };
    //             await axios.post('https://pro-customers-api.omyfood.io/api/announcements', payload);
    //         }
    //         toast.success('Announcement saved');
    //         navigate('/announcements');
    //     } catch (e) {
    //         toast.error('Save failed');
    //     }
    // };

    const handleSendNow = async () => {
        try {
            if (isEdit) {
                const payload = buildPayload('sent');
                await axios.put(`https://pro-customers-api.omyfood.io/api/announcements/${params.id}`, payload);
            } else {
                if (!vendorID) {
                    toast.error('Missing vendor id');
                    return;
                }
                const payload = { vendor_id: vendorID, ...buildPayload('sent') };
                await axios.post('https://pro-customers-api.omyfood.io/api/announcements', payload);
            }
            toast.success('Announcement sent');
            navigate('/announcements');
        } catch (e) {
            toast.error('Send failed');
        }
    };

    const filteredMenuItems = useMemo(() => {
        const q = menuSearch.trim().toLowerCase();
        if (!q) return menuItems;
        return menuItems.filter((mi) => {
            const name = String(mi.Item_name || mi.Name || '').toLowerCase();
            const desc = String(mi.Description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
        });
    }, [menuSearch, menuItems]);

    return (
        <div className="vendor-annc-page">
            <VendorSideMenuBar />
            <VendorTopMenuBar />

            <div className="dashboard-container">
                <div className="vendor-common-container">
                    <div className="vendor-annc-content">
                        <div className="annc-title">{isEdit ? 'Edit Announcement' : 'New Announcement'}</div>

                        <div className="card-white">
                            <label className="annc-label">Title</label>
                            <input className="annc-input" value={form.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Taking Orders for Tomorrow" />

                            <label className="annc-label">Message Text</label>
                            <textarea className="annc-textarea" value={form.message} onChange={(e) => updateField('message', e.target.value)} />
                        </div>

                        <div className="card-white">
                            <div className="annc-card-title">Menu Items</div>

                            {/* Selected items for this announcement */}
                            {form.items?.map((it) => (
                                <div className="annc-item-row" key={it.id}>
                                    <div className="annc-item-left">
                                        <div style={{ fontWeight: 600 }}>{it.name || 'Unnamed Item'}</div>
                                        {it.available === false && (
                                            <div style={{ color: '#b91c1c', fontSize: 12 }}>Unavailable (menu item is off)</div>
                                        )}
                                    </div>
                                    <div className="annc-item-actions" style={{ gap: 12, flexWrap: 'wrap' }}>
                                        <input
                                            type="text"
                                            className="annc-input"
                                            placeholder="Size (e.g., 28oz)"
                                            style={{ width: 140, margin: 0 }}
                                            value={it.size ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm((f) => ({
                                                    ...f,
                                                    items: f.items.map((x) => x.id === it.id ? { ...x, size: val } : x)
                                                }));
                                            }}
                                        />
                                        <input
                                            type="number"
                                            className="annc-input"
                                            placeholder="Price ($)"
                                            style={{ width: 120, margin: 0 }}
                                            value={it.price ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm((f) => ({
                                                    ...f,
                                                    items: f.items.map((x) => x.id === it.id ? { ...x, price: val } : x)
                                                }));
                                            }}
                                        />
                                        <input
                                            type="number"
                                            className="annc-input"
                                            placeholder="Qty left (optional)"
                                            style={{ width: 160, margin: 0 }}
                                            value={it.qtyLeft ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setForm((f) => ({
                                                    ...f,
                                                    items: f.items.map((x) => x.id === it.id ? { ...x, qtyLeft: val } : x)
                                                }));
                                            }}
                                        />
                                        <span className="annc-delete" onClick={() => handleDeleteItem(it.id)}>Delete</span>
                                    </div>
                                </div>
                            ))}

                            {/* Add from menu picker */}
                            <div style={{ marginTop: 12 }}>
                                <div className="annc-card-title" style={{ fontWeight: 600 }}>Add From Your Menu</div>
                                <input
                                    className="annc-input"
                                    placeholder="Search menu items..."
                                    value={menuSearch}
                                    onChange={(e) => setMenuSearch(e.target.value)}
                                />
                                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                                    {filteredMenuItems.map((mi) => {
                                        const extId = String(mi.external_item_id || '');
                                        const cm = cloverMap[extId] || {};
                                        const available = (mi.Status === 'active') && (cm.available !== false);
                                        const alreadyAdded = (form.items || []).some((x) => x.sourceMenuId === mi.ID);
                                        return (
                                            <div className="annc-item-row" key={mi.ID}>
                                                <div className="annc-item-left">
                                                    <div>{mi.Item_name}</div>
                                                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                        ${String(mi.Price ?? '')}
                                                        <span className="annc-dot">&bull;</span>
                                                        <span>{available ? 'Available' : 'Unavailable'}</span>
                                                    </div>
                                                </div>
                                                <div className="annc-item-actions">
                                                    <button
                                                        className="btn-primary"
                                                        disabled={alreadyAdded}
                                                        title={!available ? 'Item is unavailable' : (alreadyAdded ? 'Already added' : 'Add item')}
                                                        onClick={() => addFromMenu(mi)}
                                                    >
                                                        {alreadyAdded ? 'Added' : 'Add'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Manual add disabled per request */}
                            </div>

                            {/* Manual editor removed */}
                        </div>

                        <div className="card-white">
                            <label className="annc-label">Order Deadline</label>
                            <input className="annc-input" type="datetime-local" value={form.orderDeadline} onChange={(e) => updateField('orderDeadline', e.target.value)} />

                            <label className="annc-label">Delivery Window Start</label>
                            <input className="annc-input" type="datetime-local" value={form.deliveryStart} onChange={(e) => updateField('deliveryStart', e.target.value)} />

                            <label className="annc-label">Delivery Window End</label>
                            <input className="annc-input" type="datetime-local" value={form.deliveryEnd} onChange={(e) => updateField('deliveryEnd', e.target.value)} />
                        </div>

                        {/* Notification Channels and Status UI removed per request */}

                        <div className="footer-buttons">
                            <button className="btn-primary" onClick={handleSendNow}>Send Now</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AnnouncementForm;
